import { useContext, useEffect, useState } from 'react';
import { CreateUserModal } from './CreateUserModal';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  CardActions,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  TextField,
  Paper,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import { UserCard } from './UserCard';
import { EditUserModal } from './EditUserModal';
import { userApi } from '@/generated/clients';
import { UserDTO } from '@/generated';
import { useNavigate } from 'react-router-dom';
import { DefaultContainer } from '@/components/DefaultContainer';
import { ViewList, ViewListOutlined, ViewCompact, ViewCompactOutlined, Add, Edit, Delete } from '@mui/icons-material';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { UserListElement } from './UserListElement';
import { AppContext } from '@/contexts/app.context';
import { pathMap } from '@/navigation/paths';

export enum DisplayMode {
  Card,
  List,
}

const getShowUsersDisplayMode = () => localStorage.getItem('usersDisplayMode');
const setShowUsersDisplayMode = (displayMode: DisplayMode) => localStorage.setItem('usersDisplayMode', String(displayMode));

type DeleteUserDialogProps = { open: boolean; userId?: string; setOpen: (open: boolean) => void };

const DeleteUserDialog = (props: DeleteUserDialogProps) => {
  const { open, setOpen } = props;

  const handleClose = async (del: boolean) => {
    if (del && props.userId) await userApi.userDeleteUser({ id: props.userId });
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{'Delete this user?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Really delete this user?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose(true)} autoFocus>
            Delete
          </Button>
          <Button onClick={() => handleClose(false)}>Abort</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const getSortBy = () => localStorage.getItem('sortBy');
const setSortBy = (sortBy: string) => localStorage.setItem('sortBy', sortBy);

export const Users = () => {
  const navigate = useNavigate();
  const [userList, setUserList] = useState<Array<UserDTO>>();
  const [showCreateUserModal, setShowCreateUserModal] = useState<boolean>(false);
  const [showEditUserModal, setShowEditUserModal] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<UserDTO>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [userIdToDelete, setUserIdToDelete] = useState<string>();
  const [userSortFilter, setUserSortFilter] = useState<string | null | undefined>(getSortBy() ?? undefined);
  const [userSearchText, setUserSearchText] = useState<string>('');
  const [displayMode, setDisplayMode] = useState(Number(getShowUsersDisplayMode() ?? DisplayMode.Card));
  const context = useContext(AppContext);
  const user = context.state?.userProfile;

  const getUsers = async () => {
    const users = await userApi.userListUsers();
    setUserList(users);
  };

  useEffect(() => {
    if (!userList || showCreateUserModal === false) {
      getUsers();
    }
  }, [showCreateUserModal, showEditUserModal, deleteDialogOpen]);

  let filteredUsers = userList;

  if (userSortFilter === 'lastLogin')
    filteredUsers = userList?.sort((a, b) => {
      const aTime = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
      const bTime = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;

      return aTime > bTime ? -1 : 1;
    });
  else if (userSortFilter === 'name')
    filteredUsers = userList?.sort((a, b) => {
      if (a.username && b.username) return a.username > b.username ? 1 : -1;
      return a.email > b.email ? 1 : -1;
    });
  else if (userSortFilter === 'none') filteredUsers = userList;
  if (userSearchText.trim().length)
    filteredUsers = filteredUsers?.filter((fu) => {
      return (fu.username?.includes(userSearchText.trim()) ?? false) || (fu.email?.includes(userSearchText.trim()) ?? false);
    });

  return (
    <>
      <DefaultContainer>
        <Paper sx={{ my: 1, zIndex: 1, padding: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
          <Grid container sx={{ alignContent: 'center', alignItems: 'center', paddingLeft: 1 }} gap={2}>
            <Stack sx={{ alignItems: 'center' }} direction={'row'}>
              <Tooltip title={'Create new user'}>
                <IconButton onClick={() => setShowCreateUserModal(true)}>
                  <Add />
                </IconButton>
              </Tooltip>
              <Tooltip title={'Open Trainee Assignment Screen'}>
                <IconButton
                  onClick={() => {
                    navigate('/trainee-assignment');
                  }}
                >
                  <AssignmentIndIcon />
                </IconButton>
              </Tooltip>
              Sort by
              <Select
                defaultValue={'none'}
                sx={{ mx: 2 }}
                value={userSortFilter}
                onChange={(e) => {
                  setUserSortFilter(e.target.value);
                  if (e.target.value) setSortBy(e.target.value);
                }}
              >
                <MenuItem value={'none'}>Nothing</MenuItem>
                <MenuItem value={'lastLogin'}>Last Login</MenuItem>
                <MenuItem value={'name'}>User Name</MenuItem>
              </Select>
              <TextField value={userSearchText} onChange={(e: any) => setUserSearchText(e.target.value)} sx={{ mx: 2 }} id="outlined-basic" label="Search" variant="outlined" />
            </Stack>
            <Stack sx={{ flex: 1, justifyContent: 'flex-end' }} direction={'row'}>
              <IconButton
                onClick={() => {
                  setShowUsersDisplayMode(DisplayMode.List);
                  setDisplayMode(DisplayMode.List);
                }}
              >
                {displayMode === DisplayMode.List ? <ViewList /> : <ViewListOutlined />}
              </IconButton>
              <IconButton
                onClick={() => {
                  setShowUsersDisplayMode(DisplayMode.Card);
                  setDisplayMode(DisplayMode.Card);
                }}
              >
                {displayMode === DisplayMode.Card ? <ViewCompact /> : <ViewCompactOutlined />}
              </IconButton>
            </Stack>
          </Grid>
        </Paper>
        <DeleteUserDialog userId={userIdToDelete} open={deleteDialogOpen} setOpen={setDeleteDialogOpen} />
        <Grid container direction={'row'} spacing={1}>
          {filteredUsers?.map((u) => (
            <>
              {displayMode === DisplayMode.Card && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <UserCard onClick={() => navigate(`${pathMap.Decks}/${u.userId}`)} user={u} timeout={1000}>
                    <CardActions sx={{ justifyContent: 'flex-end', alignSelf: 'flex-end', flex: 0.1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={u?.active}
                            onChange={async (e) => {
                              if (user) {
                                await userApi.userUpdateUser({ updateUserDTO: { ...u, active: !u.active } });
                                getUsers();
                              }
                            }}
                          />
                        }
                        label="Active"
                      />
                      <IconButton
                        onClick={() => {
                          setEditUser(u);
                          setShowEditUserModal(true);
                        }}
                        style={{}}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={async () => {
                          setUserIdToDelete(u.userId);
                          setDeleteDialogOpen(true);
                        }}
                        style={{}}
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </UserCard>
                </Grid>
              )}
              {displayMode === DisplayMode.List && (
                <UserListElement
                  user={u}
                  timeout={10}
                  setShowDeleteModal={() => {
                    setUserIdToDelete(u.userId);
                    setDeleteDialogOpen(true);
                  }}
                  onClick={() => {
                    setEditUser(u);
                    setShowEditUserModal(true);
                  }}
                />
              )}
            </>
          ))}
        </Grid>
        <CreateUserModal showCreateUserModal={showCreateUserModal} setShowCreateUserModal={setShowCreateUserModal} />
        {editUser && (
          <EditUserModal
            user={editUser}
            show={showEditUserModal}
            setShow={(v) => {
              setShowEditUserModal(v);
              if (v === false) setEditUser(undefined);
            }}
          />
        )}
      </DefaultContainer>
    </>
  );
};
